import {put, call, all} from 'redux-saga/effects';
import Event from "../../entities/event";
import * as Actions from "../actions/eventActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type EventAction = {
    eventId: string;
    event: Event;
    events: Event[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneEvent(action: EventAction) {
	const session = tabSession.captureGeneration();
	try {
		const event: Event = yield call(api.findOne, 'event', action.eventId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.EVENT_FETCH_ONE_SUCCESS, eventId: action.eventId, event, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.EVENT_FETCH_ONE_FAILURE, eventId: action.eventId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {EventAction} action
 */
export function* saveEvent(action: EventAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ EVENT_SAVE saga', action.event?.id);

		if (action.event === undefined || action.event === null) {
			console.error('saveEvent received a null');
			throw new Error('saveEvent received a null');
		}

		if (!(action.event instanceof Event)) {
			action.event = new Event(action.event);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.event.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.event);
			const addResult = yield call(idb.add, 'event', action.event);
			if (addResult.isErr()) {
				console.error(`⮂ saveEvent: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertEventSave(action.event, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.EVENT_SAVE_FAILURE, event: action.event, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.EVENT_SAVE, event: action.event, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertEventSave(action.event, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.EVENT_SAVE_FAILURE, event: action.event, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {EventAction} action
 * @param {Event[]} action.events
 */
export function* saveManyEvents(action: EventAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ EVENT_SAVE_MANY saga', action.events.map(event => event.id).join(','));

        if (!Array.isArray(action.events)) {
            console.error('saveManyEvents expected an array, received', action.events);
            throw new Error('saveManyEvents expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveEvent), under these queue keys
		const dispatchedAddedOns: number[] = action.events.map(event => event?.addedOn);
		const revertAll = () => action.events.map((event, i) =>
			put(Actions.revertEventSave(event, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Event[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.events.length; i++) {
				const event = action.events[i];
				event.persisted = false;
				ensureOperationId(event);
				toSave.push(event);
			}

			const addResult = yield call(idb.add, 'event', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyEvents: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.EVENT_SAVE_MANY_FAILURE, events: action.events, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.EVENT_SAVE_MANY, events: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.EVENT_SAVE_MANY_FAILURE, events: action.events, status: 500, err});

			console.error(`Error saving ${action.events.length} events to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {EventAction} action
 */
export function* removeEvent(action: EventAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.event === undefined || action.event === null || !(action.event instanceof Event)) {
			console.error('removeEvent expected type Event, received', action.event);
			reportQueued(false);
			throw new Error('removeEvent expected type Event');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.event.status = 'D';
		action.event.addedOn = newHRTimestamp();
		action.event.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.event);
		const addResult = yield call(idb.add, 'event', action.event);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeEvent: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.EVENT_REMOVE_FAILURE, event: action.event, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.EVENT_REMOVE, event: action.event});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.EVENT_REMOVE_FAILURE, event: action.event, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Event.
	*
	* @param {EventAction} action
	*/
export function* restoreEvent(action: EventAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreEvent expected getCurrentUserId() to return a value');
        return;
    }

	if (action.event === undefined || action.event === null) {
        console.error('restoreEvent expected a Event, received null');
        throw new Error('restoreEvent expected a Event, received null');
    }
    else if (!(action.event instanceof Event)) action.event = new Event(action.event); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.event.status = 'U';
		action.event.addedOn = newHRTimestamp();
		action.event.addedBy = userId;
		action.event.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.event);
		const addResult = yield call(idb.add, 'event', action.event);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreEvent: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.EVENT_RESTORE_FAILURE, event: action.event, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.EVENT_RESTORE, event: action.event});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.EVENT_RESTORE_FAILURE, event: action.event, status: 500, err});

		console.error('Error restoring from database', err);
	}
}